import { useEffect, useState } from 'react';
import { DefaultOptionType } from 'antd/es/select';
import useSWR from 'swr';
import { api, HydraData } from '../../../../api/apiProvider';
import urls from '../../../../api/urls';
import { useParams } from 'react-router-dom';
import { BriefChannel, BriefManager, Media, Product, TaskStatus } from '../../../../api/types';
import { BriefDataType } from './MediaFactors';

export type MediaFactorType = 'online' | 'offline';

export default function useOptions (type: MediaFactorType, brief: BriefDataType) {
  const routeParams = useParams();
  const briefId = Number(routeParams.id) || 0;

  const [managersOptions, setManagersOptions] = useState<DefaultOptionType[]>([]);
  const [statusesOptions, setStatusesOptions] = useState<DefaultOptionType[]>([]);
  const [channelsOptions, setChannelsOptions] = useState<DefaultOptionType[]>([]);
  const [productsOptions, setProductsOptions] = useState<DefaultOptionType[]>([]);
  const [media, setMedia] = useState<Media>();

  const {
    data: managers,
  } = useSWR<HydraData>(`${urls.briefs}/${briefId}/managers?t[]=manager`, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: statuses,
  } = useSWR<HydraData>(urls.tasksStatuses, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: briefChannels,
  } = useSWR<{
    data: BriefChannel[]
  }>(briefId > 0 ? `${urls.briefChannels}?brief.id=${briefId}&t[]=channelTask&t[]=briefChannel&t[]=dict&t[]=manager&t[]=dict:product` : null, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: medias,
  } = useSWR<HydraData>(urls.medias, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (managers && managers.data) {
      setManagersOptions(managers.data.map((manager: BriefManager) => ({
        label: manager.manager.email,
        value: manager.manager['@id'],
      })));
    }
  }, [managers]);

  useEffect(() => {
    if (statuses && statuses.data) {
      setStatusesOptions(statuses.data.map((status: TaskStatus) => ({
        label: status.name,
        value: status['@id'],
      })));
    }
  }, [statuses]);

  useEffect(() => {
    if (briefChannels && briefChannels.data) {
      const selectedBriefChannels = briefChannels.data
        .filter((briefChannel: BriefChannel) => briefChannel.channel.media && briefChannel.channel.media.code === type)
        .map((briefChannel: BriefChannel) => {
          return {
            value: briefChannel.channel['@id'],
            label: briefChannel.channel.name,
            briefChannel: briefChannel['@id'],
          };
        });
      setChannelsOptions(selectedBriefChannels);
    }
  }, [briefChannels]);

  useEffect(() => {
    if (brief) {
      setProductsOptions(brief.products.map((product: Product) => ({
        value: product['@id'],
        label: product.name,
      })));
    }
  }, [brief]);

  useEffect(() => {
    if (medias && medias.data) {
      const currentMedia = medias.data.find((media: Media) => media.code === type);

      if (currentMedia) {
        setMedia(currentMedia);
      }
    }
  }, [medias]);

  return {
    managersOptions,
    statusesOptions,
    channelsOptions,
    productsOptions,
    media,
  };
}
